import type { ReactNode } from "react";

export type ProposalLanguage = "lt" | "en";

export type ProposalTexts = {
  hero: {
    validUntil: string;
    eyebrow: string;
    headlineLine1: string;
    headlineLine2: string;
    descriptionWithCompany: (company: string) => ReactNode;
    descriptionNoCompany: string;
    metaClient: string;
    metaPreparedBy: string;
    metaPreparedAt: string;
    metaValidUntil: string;
    metaProposalSuffix: (quoteNumber: string) => string;
  };
  body: {
    sectionWord: string;
    sectionLabels: {
      needs: string;
      pain_points: string;
      scope: string;
      how_we_work: string;
      outcomes: string;
      timeline: string;
      why_us: string;
      case_studies: string;
      recommended: string;
      next_steps: string;
      additional_services: string;
    };
    recommendedPlanLabel: string;
    recommendedSeatPlanLabel: string;
    recommendedIntro: string;
    seatsSuffix: (n: number) => string;
    mrrSuffix: (mrr: number) => string;
    suggestedAddons: string;
    workPlanHeader: string;
    workPlanStatusHeader: string;
    workPlanStatusPlanned: string;
    benefitsBadge: string;
    benefitItemLabel: string;
    whyUsHeadline: string;
    whyUsIntro: string;
    whyUsPricingLabel: string;
    whyUsItems: string[];
    whyUsDescriptions: string[];
    timelineDefaults: Array<{ label: string; when?: string }>;
  };
  additionalServices: {
    headline: string;
    description: string;
    perMonth: string;
    perYear: string;
    oneTime: string;
    onRequest: string;
    qtyLabel: string;
    addLabel: string;
    enabledLabel: string;
    legalNote: string;
  };
  calculator: {
    sectionLabel: string;
    headline: string;
    description: string;
    valueProps: Array<{ title: string; body: string }>;
    colService: string;
    colQty: string;
    colAmount: string;
    perSeatMonth: string;
    perMonth: string;
    from: string;
    oneTime: string;
    seatPlan: string;
    onboarding: string;
    addons: string;
    accountingSystems: string;
    totalMonthly: string;
    totalMonthlyFixed: string;
    totalMonthlyUsage: string;
    totalMonthlyUsageSub: string;
    totalAnnual: string;
    totalOneTime: string;
    annualSaveSuffix: (amount: string) => string;
    oneTimeSub: string;
    oneTimePaidOnceSub: string;
    legalNote: string;
    commitmentNote: string;
    decrease: string;
    increase: string;
    accept: string;
    question: string;
    addonFallback: (i: number) => string;
    planFallback: string;
    includesPrefix: (amount: string, unit: string | null) => string;
    downloadPdf: string;
  };
  accepted: {
    title: string;
    body: string;
  };
  feedback: {
    sectionLabel: string;
    headlinePart1: string;
    headlinePart2: string;
    intro: string;
    bullet1: string;
    bullet2: string;
    sentTitle: string;
    sentBody: string;
    writeMore: string;
    commentLabel: string;
    commentPlaceholder: string;
    nameLabel: string;
    namePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    submit: string;
    sending: string;
    legal: string;
  };
  footer: {
    eyebrow: string;
    bodyWithCompany: (company: string) => ReactNode;
    bodyNoCompany: string;
  };
  acceptDialog: {
    title: string;
    description: string;
    monthlyFee: string;
    perMonth: string;
    cancel: string;
    accept: string;
    sending: string;
    step1Title: string;
    step1Description: string;
    requirementsLabel: string;
    requirementsPlaceholder: string;
    step1Submit: string;
    step2Title: string;
    step2Description: string;
    step2Done: string;
    acceptedButton: string;
  };
  questionDialog: {
    title: string;
    description: string;
    questionLabel: string;
    questionPlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    cancel: string;
    submit: string;
    sending: string;
  };
  toasts: {
    accepted: string;
    questionSent: string;
    feedbackThanks: string;
    feedbackFailed: string;
    genericError: string;
  };
  notFound: {
    title: string;
    body: string;
  };
  landingStates: {
    contactEmail: string;
    won: {
      eyebrow: string;
      title: string;
      body: string;
    };
    lost: {
      eyebrow: string;
      title: string;
      body: string;
      cta: string;
    };
    expired: {
      eyebrow: string;
      title: string;
      body: string;
      cta: string;
    };
  };
  brandSections: {
    vision: {
      sectionLabel: string;
      eyebrow: string;
      headlineParts: Array<{ text: string; gradient?: boolean }>;
      body: string;
      bullets: Array<{ title: string; body: string }>;
    };
    dashboard: {
      sectionLabel: string;
      eyebrow: string;
      headlineParts: Array<{ text: string; gradient?: boolean }>;
      body: string;
      imageAlt: string;
    };
    integrations: {
      sectionLabel: string;
      eyebrow: string;
      headlineParts: Array<{ text: string; gradient?: boolean }>;
      body: string;
      bullets: Array<{ tag: string; title: string; body: string }>;
      footnote: string;
    };
    ai_agent: {
      sectionLabel: string;
      eyebrow: string;
      headlineParts: Array<{ text: string; gradient?: boolean }>;
      body: string;
      note: string;
      capabilities: Array<{ tag: string; title: string; body: string }>;
      chatTitle: string;
      chatStatus: string;
      chatMessages: Array<{ role: "user" | "agent"; text: string }>;
      chatPlaceholder: string;
    };
    feedback_loop: {
      sectionLabel: string;
      eyebrow: string;
      headlineParts: Array<{ text: string; gradient?: boolean }>;
      body: string;
      bullets: Array<{ title: string; body: string }>;
      mockupFunkcijos: string;
      mockupSubmit: string;
      mockupWeAsk: string;
      mockupTitle: string;
      mockupSub: string;
      mockupItems: Array<{ tagA: string; tagB: string; title: string; votes: number }>;
      mockupRealtime: string;
      mockupRealtimeBody: string;
      ctaButton: string;
    };
  };
};

const LT: ProposalTexts = {
  hero: {
    validUntil: "galioja iki",
    eyebrow: "Komercinis pasiūlymas — apskaitos automatizavimas",
    headlineLine1: "Kol Jūsų konkurentai delsia,",
    headlineLine2: "mes vedame Jūsų verslą į apskaitos ateitį",
    descriptionWithCompany: (company) => (
      <>
        Šis dokumentas parengtas{" "}
        <span className="font-semibold text-white">{company}</span> komandai —
        kaip Noium gali pakeisti jūsų buhalterių kasdienybę ir kiek tai kainuoja.
      </>
    ),
    descriptionNoCompany:
      "Šis dokumentas parengtas jūsų komandai — kaip Noium gali pakeisti jūsų buhalterių kasdienybę ir kiek tai kainuoja.",
    metaClient: "Klientas",
    metaPreparedBy: "Parengė",
    metaPreparedAt: "Parengta",
    metaValidUntil: "Galioja iki",
    metaProposalSuffix: (quoteNumber) => `Pasiūlymas ${quoteNumber}`,
  },
  body: {
    sectionWord: "Skyrius",
    sectionLabels: {
      needs: "Jūsų poreikiai",
      pain_points: "Iššūkiai",
      scope: "Darbų apimtis",
      how_we_work: "Kaip dirbame",
      outcomes: "Naudos kurias pasieksite su Noium",
      timeline: "Noium sistemos adaptavimo ir integracijų planas",
      why_us: "Kodėl Noium",
      case_studies: "Klientų atsiliepimai",
      recommended: "Rekomenduojama narystė",
      next_steps: "Kas toliau",
      additional_services: "Prie pagrindinių paslaugų taip pat siūlome",
    },
    recommendedPlanLabel: "Planas",
    recommendedSeatPlanLabel: "Vietų planas",
    recommendedIntro:
      "Atsižvelgdami į jūsų poreikius ir iššūkius, rekomenduojame šį Noium narystės planą — jis labiausiai tinka jūsų komandos dydžiui ir darbų apimčiai.",
    seatsSuffix: (n) => `~${n} vietos`,
    mrrSuffix: (mrr) => `€${mrr} / mėn. įvertinimas`,
    suggestedAddons: "Siūlomi papildymai",
    workPlanHeader: "Darbų etapai",
    workPlanStatusHeader: "Statusas",
    workPlanStatusPlanned: "Suplanuota",
    benefitsBadge: "Nauda",
    benefitItemLabel: "Nauda",
    whyUsHeadline: "Kodėl Noium",
    whyUsIntro:
      "Pasirenkate ne tik įrankį — pasirenkate komandą, kuri kartu su jumis kuria buhalterijos ateitį.",
    whyUsPricingLabel: "Komercinė pasiūlymo dalis",
    whyUsItems: [
      "Ne produktas — kelias į AI buhalterijoje",
      "NIS2/TIS2 atitiktis iš karto",
      "Įrankis, kurį jūsų komanda nori naudoti",
      "Pritaikyta mažoms komandoms",
      "Greitas paleidimas",
      "Vienintelė tokia platforma rinkoje",
    ],
    whyUsDescriptions: [
      "Noium nėra dar viena programa — tai partnerystė, padedanti jūsų buhalterijai žingsnis po žingsnio pereiti į AI eros darbo būdą.",
      "Šifravimas, prieigos kontrolė ir audito žurnalas įdiegti iš karto — ES direktyvų reikalavimai padengti be papildomų modulių ar mokamų priedų.",
      "Sukurta taip, kad jūsų buhalteriai patys norėtų ja naudotis — nes ji pašalina kasdienio darbo trintį, o ne todėl, kad vadovybė liepia.",
      "Apmokysime jūsų 5 žmonių komandą ir palydėsime pirmosiomis savaitėmis, kol sistema taps kasdiene rutina.",
      "Nuo sprendimo iki veikiančios sistemos — kelios savaitės, ne mėnesiai. Jūs greitai matote rezultatus, o mes palydime kiekvieną žingsnį.",
      "Jokia kita platforma nėra taip stipriai orientuota į apskaitos verslą, jo vadovus, jų pelną ir buhalterių profesiją, kad ji taptų efektyvesnė ir patrauklesnė.",
    ],
    timelineDefaults: [
      {
        label: "Priėmus pasiūlymą — per 1 dieną paruošiame sutartį.",
        when: "1 diena",
      },
      {
        label: "Jau kitą savaitę organizuojame onboardingo plano skambutį.",
        when: "1 savaitė",
      },
    ],
  },
  additionalServices: {
    headline: "Prie pagrindinių paslaugų taip pat siūlome.",
    description:
      "Papildomi servisai, kuriuos galėsite įjungti pagal poreikį — be sutarties keitimo. Žemiau orientacinės kainos, tikslų sumą pateikiame prieš įjungimą.",
    perMonth: "/ mėn.",
    perYear: "/ metams",
    oneTime: "vienkartinis",
    onRequest: "Pagal poreikį",
    qtyLabel: "Kiekis",
    addLabel: "Pridėti",
    enabledLabel: "Įjungta",
    legalNote: "Kainos be PVM. Servisai įjungiami atskiru susitarimu.",
  },
  calculator: {
    sectionLabel: "Pasiūlymas",
    headline: "Kaina, kuri prisitaiko prie Jūsų verslo augimo.",
    description:
      "Keiskite kiekius — suma persiskaičiuoja automatiškai. Paslaugas galite įjungti ar išjungti bet kada savitarnoje.",
    valueProps: [
      {
        title: "Aktyvuokite atskirai kiekvienam klientui",
        body: "Kiekvienam klientui galite paslaugas aktyvuoti atskirai — pagal Jūsų ir kliento galimybes.",
      },
      {
        title: "Vienas mėnuo nemokamai",
        body: "Mokėdami už metus į priekį, gaunate vieną mėnesį nemokamai.",
      },
      {
        title: "Klientai gali mokėti patys",
        body: "Už kai kurias paslaugas Jūsų klientai gali atsiskaityti tiesiogiai — per savitarną.",
      },
    ],
    colService: "Paslauga",
    colQty: "Kiekis",
    colAmount: "Suma",
    perSeatMonth: "/ vietai / mėn.",
    perMonth: "/ mėn.",
    from: "Nuo",
    oneTime: "vienkartinis",
    seatPlan: "Vietų planas",
    onboarding: "Įdiegimas ir paleidimas",
    addons: "Papildomos funkcijos",
    accountingSystems: "Buhalterinė sistema",
    totalMonthly: "Mėnesinis",
    totalMonthlyFixed: "Pastovus mėnesinis mokestis",
    totalMonthlyUsage: "Kintantis mėnesinis mokestis (Spėjamas)",
    totalMonthlyUsageSub: "Apmokestinama pagal faktinį naudojimą",
    totalAnnual: "Metinis (-20%)",
    totalOneTime: "Vienkartinis",
    annualSaveSuffix: (amount) => `Sutaupote ${amount} per metus`,
    oneTimeSub: "Įdiegimas ir paleidimas — nemokami",
    oneTimePaidOnceSub: "Mokamas vieną kartą",
    legalNote:
      "Kainos be PVM. Apmokestinama mėnesiui pagal faktinį naudojimą. Kiekius galite keisti bet kada savitarnoje.",
    commitmentNote:
      "Patvirtindami pasiūlymą tik suteikiate signalą apie savo sprendimą — jokių sutarčių, mokėjimų ar kitų procedūrų kol kas neaktyvuojama.",
    decrease: "Mažinti",
    increase: "Didinti",
    accept: "Priimti pasiūlymą",
    question: "Turiu klausimų",
    addonFallback: (i) => `Papildymas ${i + 1}`,
    planFallback: "Planas",
    includesPrefix: (amount, unit) =>
      unit ? `Įskaičiuota ${amount} ${unit}. Toliau` : `Įskaičiuota ${amount}. Toliau`,
    downloadPdf: "Atsisiųsti PDF",
  },
  accepted: {
    title: "Pasiūlymas priimtas.",
    body: "Ačiū. Mūsų komanda netrukus susisieks dėl sutarties ir tolimesnių žingsnių.",
  },
  feedback: {
    sectionLabel: "Jūsų atsiliepimas",
    headlinePart1: "Pasakykite,",
    headlinePart2: "ką manote.",
    intro:
      "Parašykite, ko trūksta pasiūlyme, kas atrodo ne taip arba kas paliko geriausią įspūdį. Jūsų žinutė tiesiai keliauja į mūsų komandos užrašus — tai padeda mums pataikyti į taikinį.",
    bullet1: "Atsakome darbo dienomis, paprastai per kelias valandas.",
    bullet2: "Drąsiai rašykite savo kalba, tas kas „ant liežuvio“.",
    sentTitle: "Ačiū už atsiliepimą.",
    sentBody: "Mūsų komanda gavo jūsų pastabas ir jas peržiūrės.",
    writeMore: "Rašyti dar",
    commentLabel: "Komentaras",
    commentPlaceholder: "Kas patiko, ko trūksta, ką pakeistumėte...",
    nameLabel: "Vardas (neprivaloma)",
    namePlaceholder: "Vardas Pavardė",
    emailLabel: "El. paštas (neprivaloma)",
    emailPlaceholder: "jus@imone.com",
    submit: "Siųsti atsiliepimą",
    sending: "Siunčiama...",
    legal:
      "Pateikdami atsiliepimą sutinkate, kad jis bus saugomas mūsų sistemoje ir naudojamas susisiekti dėl šio pasiūlymo.",
  },
  footer: {
    eyebrow: "Turite klausimų?",
    bodyWithCompany: (company) => (
      <>
        Šis pasiūlymas pritaikytas individualiems{" "}
        <span className="font-semibold text-white">{company}</span> poreikiams.
        Jei kažką pražiūrėjome arba ne taip supratome — parašykite, ir kartu
        viską sutvarkysime.
      </>
    ),
    bodyNoCompany:
      "Šis pasiūlymas pritaikytas individualiems jūsų poreikiams. Jei kažką pražiūrėjome arba ne taip supratome — parašykite, ir kartu viską sutvarkysime.",
  },
  acceptDialog: {
    title: "Priimti pasiūlymą?",
    description:
      'Paspaudę "Priimti" patvirtinate susidomėjimą, ir mūsų komanda susisieks dėl sutarties.',
    monthlyFee: "Mėnesinis mokestis",
    perMonth: "/mėn.",
    cancel: "Atšaukti",
    accept: "Priimti pasiūlymą",
    sending: "Siunčiama...",
    step1Title: "Labai džiaugiamės jūsų sprendimu rinktis Noium!",
    step1Description:
      "Ačiū, kad pasitikite Noium komanda. Prieš pradėdami, ar turite kokių nors specifinių reikalavimų ar pageidavimų, kuriuos turėtume žinoti?",
    requirementsLabel: "Jūsų reikalavimai ar pageidavimai (neprivaloma)",
    requirementsPlaceholder:
      "Pvz. konkrečios integracijos, terminai, komandos dydis, specifiniai procesai...",
    step1Submit: "Tęsti",
    step2Title: "Norėtumėte susitarti pokalbį?",
    step2Description:
      "Pasirinkite jums patogų laiką trumpam pokalbiui — aptarsime produktą, sutartį ir tolimesnius žingsnius.",
    step2Done: "Baigti",
    acceptedButton: "Pasiūlymas priimtas",
  },
  questionDialog: {
    title: "Turiu klausimų",
    description: "Parašykite savo klausimą — atsakysime per vieną darbo dieną.",
    questionLabel: "Klausimas",
    questionPlaceholder: "Klauskite bet ko apie pasiūlymą...",
    emailLabel: "El. paštas (neprivaloma)",
    emailPlaceholder: "jus@imone.com",
    cancel: "Atšaukti",
    submit: "Siųsti klausimą",
    sending: "Siunčiama...",
  },
  toasts: {
    accepted: "Pasiūlymas priimtas. Netrukus susisieksime.",
    questionSent: "Klausimas išsiųstas. Atsakysime per vieną darbo dieną.",
    feedbackThanks: "Ačiū už atsiliepimą — gavome.",
    feedbackFailed: "Nepavyko išsiųsti atsiliepimo",
    genericError: "Klaida",
  },
  notFound: {
    title: "Pasiūlymas nerastas",
    body: "Patikrinkite nuorodą arba susisiekite su mumis.",
  },
  landingStates: {
    contactEmail: "sales@noium.com",
    won: {
      eyebrow: "Sveikiname",
      title: "Puikios naujienos! Savo kelionei pasirinkote Noium.",
      body: "Džiaugiamės, kad būsime kartu. Mūsų komanda jau ruošiasi pradėti — netrukus su jumis susisieksime dėl tolesnių žingsnių.",
    },
    lost: {
      eyebrow: "Pasiūlymas uždarytas",
      title: "Deja, šįkart nepasirinkote Noium.",
      body: "Suprantame — laikas ne visada tinkamas. Jei norėtumėte grįžti prie šio pasiūlymo, mielai jį atnaujinsime.",
      cta: "Susisiekite, kad atnaujintume",
    },
    expired: {
      eyebrow: "Pasiūlymas nebegalioja",
      title: "Deja, šio pasiūlymo galiojimas pasibaigė.",
      body: "Pasiūlymo galiojimo laikas baigėsi. Jei vis dar domitės, mielai paruošime atnaujintą pasiūlymą.",
      cta: "Susisiekite, kad atnaujintume",
    },
  },
  brandSections: {
    vision: {
      sectionLabel: "Apie Noium",
      eyebrow: "Vizija · 2026 →",
      headlineParts: [
        { text: "Netrukus Noium taps " },
        { text: "vienintele apskaitos operacijų platforma regione", gradient: true },
        { text: ", valdoma DI agento." },
      ],
      body:
        "Iki 2026 m. pabaigos visos Noium operacijos — dokumentų rūšiavimas, banko išrašai, deklaracijų terminai, klientų komunikacija — orkestruojamos vieno DI agento. Buhalteris išlieka sprendimų priėmėju; agentas perima rutiną.",
      bullets: [
        {
          title: "Bendrauja su klientais",
          body:
            "Klausia trūkstamų dokumentų, primena terminus, atsako į kasdienius klausimus — buhalterio vardu.",
        },
        {
          title: "Bendrauja su Jūsų duomenimis ir dokumentais",
          body:
            "Skaito sąskaitas, sutartis ir banko išrašus; klasifikuoja, susieja su klientu ir išrenka reikiamą informaciją.",
        },
        {
          title: "Ieško informacijos",
          body:
            "Greitai randa atsakymą tarp tūkstančių dokumentų ir kliento istorijoje — be rankinės paieškos.",
        },
      ],
    },
    dashboard: {
      sectionLabel: "Produkto vaizdas",
      eyebrow: "Produkto vaizdas · Gyvas dashboard'as",
      headlineParts: [
        { text: "Taip galėtų atrodyti " },
        { text: "Jūsų kasdieninė", gradient: true },
        { text: " darbo erdvė." },
      ],
      body:
        "Kairėje — Noium agentas, su kuriuo bendraujama natūralia kalba. Centre — klientų ir užduočių navigacija. Dešinėje — kliento kortelė su dokumentais, automatizacijomis ir terminais.",
      imageAlt: "Noium produkto vaizdas",
    },
    integrations: {
      sectionLabel: "Integracijos",
      eyebrow: "Integracijos",
      headlineParts: [
        { text: "Viena platforma — " },
        { text: "visi Jūsų įrankiai", gradient: true },
        { text: "." },
      ],
      body:
        "Jungiamės prie sistemų, su kuriomis Jūsų komanda jau dirba — apskaita, dokumentų saugykla ir kasdieniai įrankiai vienoje darbo eigoje.",
      bullets: [
        {
          tag: "Dokumentų srautas",
          title: "Dokumentai keliauja automatiškai nuo kliento iki apskaitos",
          body:
            "Klientas atsiunčia sąskaitą — ji pati atsiranda apskaitos sistemoje. Be persiuntimų, be kopijavimo, be rankinio vedimo.",
        },
        {
          tag: "Archyvas",
          title: "Dokumentai archyvuojami automatiškai Jūsų dokumentų saugykloje",
          body:
            "Visi dokumentai sutvarkomi ir patalpinami į SharePoint ar Drive — pas tinkamą klientą ir tinkamą periodą. Jums nereikia daryti nieko.",
        },
        {
          tag: "Pagalbiniai įrankiai",
          title: "Skaitmenizacijos ir kiti pagalbiniai įrankiai",
          body:
            "Skenavimas, dokumentų atpažinimas, priminimai, banko išrašai — viskas po ranka, kad rutiną perimtų sistema, o ne žmogus.",
        },
      ],
      footnote:
        "Nematote savo sistemos? Padarome jungtį per 2–4 savaites.",
    },
    ai_agent: {
      sectionLabel: "AI agentas",
      eyebrow: "AI agentas · Noium platformos branduolys",
      headlineParts: [
        { text: "Vienas " },
        { text: "agentas", gradient: true },
        { text: ", kuris dirba kartu su Jūsų komanda." },
      ],
      body:
        "Noium AI agentas perima rutiną — bendrauja su klientais, klasifikuoja dokumentus, ruošia juodraščius. Jūs paliekate sprendimų priėmimą sau, agentas paliečia viską, kas tarp jų.",
      note:
        "Mes vis dar dirbame prie šios funkcijos ir iki 2026 m. pabaigos visos Noium operacijos — dokumentų rūšiavimas, banko išrašai, deklaracijų terminai, klientų komunikacija — bus orkestruojamos vieno DI agento. Buhalteris išlieka sprendimų priėmėju; agentas perima rutiną.",
      capabilities: [
        {
          tag: "Rašo žinutes",
          title: "Bendrauja su klientais",
          body: "Primena terminus, prašo trūkstamų dokumentų, atsako į kasdienius klausimus — buhalterio vardu.",
        },
        {
          tag: "Skaito dokumentus",
          title: "Klasifikuoja ir susieja",
          body: "Sąskaitos, sutartys, banko išrašai — automatiškai prijungiami prie kliento ir reikiamo periodo.",
        },
        {
          tag: "Atsako į klausimus",
          title: "Ieško informacijos",
          body: "Greitai randa atsakymą tarp tūkstančių dokumentų ir kliento istorijoje — be rankinės paieškos.",
        },
      ],
      chatTitle: "Noium agentas",
      chatStatus: "Aktyvus",
      chatMessages: [
        { role: "user", text: "Ar UAB „Vėjas“ jau atsiuntė balandžio sąskaitas?" },
        { role: "agent", text: "Dar ne. Vakar išsiunčiau priminimą; trūksta 3 pirkimo sąskaitų. Klientas atsakė, kad pateiks iki rytojaus 12:00." },
        { role: "user", text: "Paruošk PVM deklaracijos juodraštį už balandį." },
        { role: "agent", text: "Paruošta. Pajamos 48 320 €, sąnaudos 31 720 €, mokėtinas PVM 3 486 €. Atidaryti peržiūrai →" },
      ],
      chatPlaceholder: "Parašykite agentui...",
    },
    feedback_loop: {
      sectionLabel: "Grįžtamasis ryšys",
      eyebrow: "Noium vertybė · Vidinis principas",
      headlineParts: [
        { text: "Svarbiausia — " },
        { text: "grįžtamasis ryšys", gradient: true },
        { text: " ir judėjimas į priekį." },
      ],
      body:
        "Mes prisitaikome prie kliento poreikių. Tam sukūrėme automatizuotą grįžtamojo ryšio portalą, kuriame matomas visas darbų planas, pranešimai apie klaidas — viskas tiesiogiai susieta su produkto komanda.",
      bullets: [
        {
          title: "Viskas atvirai",
          body:
            "Visas darbų planas, statusai ir laukiamų funkcijų eilė — matomi realiu laiku.",
        },
        {
          title: "Vienas mygtukas pranešti",
          body: "Klaidą ar pageidavimą registruojate iš programos vienu paspaudimu — be el. laiškų.",
        },
        {
          title: "Tiesiogiai produkto komandai",
          body:
            "Jūsų balsas keliauja į produkto komandą be tarpininkų — ir Jūs matote, kada jis virsta darbu.",
        },
      ],
      mockupFunkcijos: "Funkcijos",
      mockupSubmit: "Pateikti",
      mockupWeAsk: "Mes klausome",
      mockupTitle: "Padėk tobulinti Noium",
      mockupSub: "Balsuokite už svarbiausia, siūlykite, ko trūksta, ir stebėkite, kaip Jūsų idėjos tampa realybe.",
      mockupItems: [
        { tagA: "Suplanuota", tagB: "Integracijos", title: "Microsoft SharePoint", votes: 2 },
        { tagA: "Vykdoma", tagB: "Mokesčiai", title: "Deklaracijų žemėlapis", votes: 5 },
        { tagA: "Kandidatė", tagB: "Įmonės valdymas", title: "Kalendoriaus valdymas", votes: 3 },
        { tagA: "Idėja", tagB: "Mokymai", title: "Darbuotojų procedūros", votes: 1 },
      ],
      mockupRealtime: "Realiu laiku",
      mockupRealtimeBody: "Pateiktas balsas iškart matomas produkto komandai.",
      ctaButton: "Peržiūrėkite kaip tai atrodo",
    },
  },
};

const EN: ProposalTexts = {
  hero: {
    validUntil: "valid until",
    eyebrow: "Commercial proposal — accounting automation",
    headlineLine1: "While your competitors hesitate,",
    headlineLine2: "we lead your business into the future of accounting.",
    descriptionWithCompany: (company) => (
      <>
        This document was prepared for the{" "}
        <span className="font-semibold text-white">{company}</span> team — how
        Noium can transform your accountants' day-to-day, and what it costs.
      </>
    ),
    descriptionNoCompany:
      "This document was prepared for your team — how Noium can transform your accountants' day-to-day, and what it costs.",
    metaClient: "Client",
    metaPreparedBy: "Prepared by",
    metaPreparedAt: "Prepared",
    metaValidUntil: "Valid until",
    metaProposalSuffix: (quoteNumber) => `Proposal ${quoteNumber}`,
  },
  body: {
    sectionWord: "Section",
    sectionLabels: {
      needs: "Your needs",
      pain_points: "Challenges",
      scope: "Scope of work",
      how_we_work: "How we work",
      outcomes: "What you'll achieve with Noium",
      timeline: "Noium implementation plan",
      why_us: "Why Noium",
      case_studies: "Case studies",
      recommended: "Recommended membership",
      next_steps: "Next steps",
      additional_services: "We also offer alongside the main services",
    },
    recommendedPlanLabel: "Plan",
    recommendedSeatPlanLabel: "Seat plan",
    recommendedIntro:
      "Based on your needs and challenges, we recommend this Noium membership plan — it fits your team size and the scope of work best.",
    seatsSuffix: (n) => `~${n} seats`,
    mrrSuffix: (mrr) => `€${mrr} / mo estimated`,
    suggestedAddons: "Suggested add-ons",
    workPlanHeader: "Phases",
    workPlanStatusHeader: "Status",
    workPlanStatusPlanned: "Planned",
    benefitsBadge: "Benefits",
    benefitItemLabel: "Benefit",
    whyUsHeadline: "Why Noium",
    whyUsIntro:
      "You're picking more than a tool — you're picking a team that builds the future of accounting alongside you.",
    whyUsPricingLabel: "Commercial section of the proposal",
    whyUsItems: [
      "Not a product — a path to AI in accounting",
      "NIS2/TIS2 compliance out of the box",
      "A tool your team actually wants to use",
      "Built for small teams",
      "Fast launch",
      "The only platform of its kind",
    ],
    whyUsDescriptions: [
      "Noium isn't just another piece of software — it's a partnership that walks your firm into AI-driven accounting, step by step.",
      "Encryption, access control and audit logs are built in from day one — EU directives are covered without extra modules or paid add-ons.",
      "Designed so your accountants actually want to use it — because it removes friction from their daily work, not because management forces them to.",
      "A 5-person firm gets the leverage of a much larger one — no heavyweight rollout, no months of training.",
      "Weeks from decision to a working system, not months — you see results quickly and we walk alongside every step.",
      "No other platform is so deeply focused on the accounting business — its leaders, their bottom line and the accountants' profession — to make the profession more efficient and more attractive.",
    ],
    timelineDefaults: [
      {
        label: "Once you accept — we draft the contract within 1 day.",
        when: "Day 1",
      },
      {
        label: "The following week we run an onboarding planning call.",
        when: "Week 1",
      },
    ],
  },
  additionalServices: {
    headline: "We also offer alongside the main services.",
    description:
      "Optional add-on services you can enable as needs grow — no contract change required. Indicative pricing below; we confirm the exact figure before activation.",
    perMonth: "/ mo",
    perYear: "/ yr",
    oneTime: "one-time",
    onRequest: "On request",
    qtyLabel: "Qty",
    addLabel: "Add",
    enabledLabel: "Enabled",
    legalNote: "Prices exclude VAT. Services are enabled by separate agreement.",
  },
  calculator: {
    sectionLabel: "Project proposal",
    headline: "Pricing that adapts to your business growth.",
    description:
      "Change quantities — the total recalculates automatically. You can turn services on or off any time from self-service.",
    valueProps: [
      {
        title: "Activate per client",
        body: "Turn each service on independently for each of your clients — at the pace that works for both of you.",
      },
      {
        title: "One month free",
        body: "Pay annually and get one month on the house.",
      },
      {
        title: "Clients can pay themselves",
        body: "Some services can be charged directly to your clients — through self-service.",
      },
    ],
    colService: "Service",
    colQty: "Qty",
    colAmount: "Amount",
    perSeatMonth: "/ seat / mo",
    perMonth: "/ mo",
    from: "From",
    oneTime: "one-time",
    seatPlan: "Seat plan",
    onboarding: "Onboarding & setup",
    addons: "Add-ons",
    accountingSystems: "Accounting system",
    totalMonthly: "Monthly",
    totalMonthlyFixed: "Fixed monthly fee",
    totalMonthlyUsage: "Variable monthly fee (Estimated)",
    totalMonthlyUsageSub: "Billed on actual usage",
    totalAnnual: "Annual (-20%)",
    totalOneTime: "One-time",
    annualSaveSuffix: (amount) => `Save ${amount} / year`,
    oneTimeSub: "Onboarding and setup — free",
    oneTimePaidOnceSub: "Paid once at signing",
    legalNote:
      "Prices exclude VAT. Billed monthly based on actual usage. Quantities can be changed any time from self-service.",
    commitmentNote:
      "By approving this quote you're simply giving us a signal about your decision — no agreements, payments, or any other procedures are activated yet.",
    decrease: "Decrease",
    increase: "Increase",
    accept: "Accept proposal",
    question: "I have questions",
    addonFallback: (i) => `Add-on ${i + 1}`,
    planFallback: "Plan",
    includesPrefix: (amount, unit) =>
      unit ? `Includes ${amount} ${unit}. Then` : `Includes ${amount}. Then`,
    downloadPdf: "Download PDF",
  },
  accepted: {
    title: "Proposal accepted.",
    body: "Thank you. Our team will be in touch shortly about the contract and next steps.",
  },
  feedback: {
    sectionLabel: "Your feedback",
    headlinePart1: "Tell us",
    headlinePart2: "what you think.",
    intro:
      "Write what's missing from the proposal, what feels off, or what left the best impression. Your message goes straight to our team's notes — it helps us hit the mark.",
    bullet1: "We reply on weekdays, usually within a few hours.",
    bullet2: "No need to be formal — just write it the way you'd say it.",
    sentTitle: "Thanks for the feedback.",
    sentBody: "Our team has received your notes and will work on them.",
    writeMore: "Write more",
    commentLabel: "Comment",
    commentPlaceholder: "What worked, what's missing, what you'd change...",
    nameLabel: "Name (optional)",
    namePlaceholder: "Full name",
    emailLabel: "Email (optional)",
    emailPlaceholder: "you@company.com",
    submit: "Send feedback",
    sending: "Sending...",
    legal:
      "By submitting feedback, you agree that it will be stored in our system and used to follow up about this proposal.",
  },
  footer: {
    eyebrow: "Got questions?",
    bodyWithCompany: (company) => (
      <>
        This proposal was tailored to{" "}
        <span className="font-semibold text-white">{company}</span> individual
        needs. If we missed anything or got something wrong — reach out, and
        we'll fix it together.
      </>
    ),
    bodyNoCompany:
      "This proposal was tailored to your individual needs. If we missed anything or got something wrong — reach out, and we'll fix it together.",
  },
  acceptDialog: {
    title: "Accept proposal?",
    description:
      'Clicking "Accept" confirms your interest and our team will reach out about the contract.',
    monthlyFee: "Monthly fee",
    perMonth: "/mo",
    cancel: "Cancel",
    accept: "Accept proposal",
    sending: "Sending...",
    step1Title: "We're thrilled you chose Noium!",
    step1Description:
      "Thank you for trusting the Noium team. Before we get started, do you have any specific requirements or preferences we should know about?",
    requirementsLabel: "Your requirements or preferences (optional)",
    requirementsPlaceholder:
      "e.g. specific integrations, deadlines, team size, particular processes...",
    step1Submit: "Continue",
    step2Title: "Would you like to book a call?",
    step2Description:
      "Pick a time that works for you for a short call — we'll walk through the product, the contract and next steps.",
    step2Done: "Finish",
    acceptedButton: "Proposal accepted",
  },
  questionDialog: {
    title: "I have questions",
    description: "Write your question — we'll reply within one business day.",
    questionLabel: "Question",
    questionPlaceholder: "Ask anything about the proposal...",
    emailLabel: "Email (optional)",
    emailPlaceholder: "you@company.com",
    cancel: "Cancel",
    submit: "Send question",
    sending: "Sending...",
  },
  toasts: {
    accepted: "Proposal accepted. We'll be in touch shortly.",
    questionSent: "Question sent. We'll reply within one business day.",
    feedbackThanks: "Thanks for the feedback — we got it.",
    feedbackFailed: "Failed to send feedback",
    genericError: "Error",
  },
  notFound: {
    title: "Proposal not found",
    body: "Check the link or get in touch with us.",
  },
  landingStates: {
    contactEmail: "sales@noium.com",
    won: {
      eyebrow: "Congratulations",
      title: "Good news! You've chosen Noium for your journey.",
      body: "We're thrilled to be on board with you. Our team is already getting ready to kick things off — we'll be in touch shortly with the next steps.",
    },
    lost: {
      eyebrow: "Proposal closed",
      title: "Unfortunately, you didn't choose Noium.",
      body: "We get it — the timing isn't always right. If you'd like to revisit this proposal, we'd be glad to reopen the case for you.",
      cta: "Contact us to reopen the case",
    },
    expired: {
      eyebrow: "Proposal expired",
      title: "Unfortunately, this quote has expired.",
      body: "This proposal is no longer valid. If you're still interested, we'd be happy to put together a fresh quote for you.",
      cta: "Contact us to reopen the case",
    },
  },
  brandSections: {
    vision: {
      sectionLabel: "About Noium",
      eyebrow: "Vision · 2026 →",
      headlineParts: [
        { text: "Noium is becoming " },
        { text: "the region's single platform for accounting operations", gradient: true },
        { text: ", driven by an AI agent." },
      ],
      body:
        "By the end of 2026 every Noium operation — document classification, bank statements, return deadlines, client conversations — is orchestrated by one AI agent. The accountant stays the decision-maker; the agent runs the routine.",
      bullets: [
        {
          title: "Talks to your clients",
          body: "Asks for missing documents, nudges deadlines, answers everyday questions — on behalf of your team.",
        },
        {
          title: "Reads your data and documents",
          body: "Parses invoices, contracts and bank statements; classifies them, links to the right client, pulls out what matters.",
        },
        {
          title: "Finds what you need",
          body: "Surfaces the answer across thousands of documents and the client's history — no manual hunting.",
        },
      ],
    },
    dashboard: {
      sectionLabel: "Product view",
      eyebrow: "Product view · Live dashboard",
      headlineParts: [
        { text: "What your " },
        { text: "everyday", gradient: true },
        { text: " workspace could look like." },
      ],
      body:
        "On the left — the Noium agent, talked to in plain language. In the middle — client and task navigation. On the right — the client record with documents, automations and deadlines.",
      imageAlt: "Noium product view",
    },
    integrations: {
      sectionLabel: "Integrations",
      eyebrow: "Integrations",
      headlineParts: [
        { text: "One platform — " },
        { text: "all of your tools", gradient: true },
        { text: "." },
      ],
      body:
        "We connect to the systems your team already uses — accounting, document storage and everyday tools woven into one workflow.",
      bullets: [
        {
          tag: "Document flow",
          title: "Documents move automatically from client to accounting",
          body:
            "Your client sends an invoice — it lands in the accounting system on its own. No forwarding, no copying, no manual entry.",
        },
        {
          tag: "Archive",
          title: "Documents archived automatically in your document storage",
          body:
            "Everything is sorted and filed into SharePoint or Drive — under the right client and the right period. Nothing for you to do.",
        },
        {
          tag: "Helper tools",
          title: "Digitisation and other helper tools",
          body:
            "Scanning, document recognition, reminders, bank statements — all in one place, so the routine runs on the system, not on people.",
        },
      ],
      footnote:
        "Don't see your system? We build connectors in 2–4 weeks.",
    },
    ai_agent: {
      sectionLabel: "AI agent",
      eyebrow: "AI agent · The core of the Noium platform",
      headlineParts: [
        { text: "One " },
        { text: "agent", gradient: true },
        { text: " that works alongside your team." },
      ],
      body:
        "The Noium AI agent runs the routine — talks to clients, classifies documents, drafts returns. You keep the decisions; the agent handles everything between them.",
      note:
        "We're still working on this feature. By the end of 2026 every Noium operation — document classification, bank statements, return deadlines, client conversations — will be orchestrated by one AI agent. The accountant stays the decision-maker; the agent runs the routine.",
      capabilities: [
        {
          tag: "Writes messages",
          title: "Talks to clients",
          body: "Nudges deadlines, asks for missing documents, answers everyday questions — on your team's behalf.",
        },
        {
          tag: "Reads documents",
          title: "Classifies and links",
          body: "Invoices, contracts, bank statements — auto-attached to the right client and period.",
        },
        {
          tag: "Answers questions",
          title: "Finds what you need",
          body: "Surfaces the answer across thousands of documents and the client's history — no manual hunting.",
        },
      ],
      chatTitle: "Noium agent",
      chatStatus: "Active",
      chatMessages: [
        { role: "user", text: "Has UAB Vėjas already sent April's invoices?" },
        { role: "agent", text: "Not yet. I sent a reminder yesterday; 3 purchase invoices are missing. The client replied they'll send them by tomorrow 12:00." },
        { role: "user", text: "Draft the VAT return for April." },
        { role: "agent", text: "Draft ready. Revenue 48,320 €, expenses 31,720 €, VAT due 3,486 €. Open for review →" },
      ],
      chatPlaceholder: "Message the agent...",
    },
    feedback_loop: {
      sectionLabel: "Feedback loop",
      eyebrow: "Noium principle · How we work",
      headlineParts: [
        { text: "What matters most — " },
        { text: "feedback", gradient: true },
        { text: " and momentum." },
      ],
      body:
        "We adapt the product to your team. That's why every Noium plan ships with an automated feedback portal: open roadmap, error reports, requests — all routed straight to the product team.",
      bullets: [
        { title: "Everything open", body: "The full work plan, statuses and upcoming features — visible in real time." },
        { title: "One-click reporting", body: "Bugs and requests file from inside the product with a single click — no email threads." },
        { title: "Straight to the product team", body: "Your voice reaches the product team with no middlemen — and you watch it turn into work." },
      ],
      mockupFunkcijos: "Features",
      mockupSubmit: "Submit",
      mockupWeAsk: "We're listening",
      mockupTitle: "Help us improve Noium",
      mockupSub: "Vote on what matters, suggest what's missing, and watch your ideas become reality.",
      mockupItems: [
        { tagA: "Planned", tagB: "Integrations", title: "Microsoft SharePoint", votes: 2 },
        { tagA: "In progress", tagB: "Tax", title: "Declarations map", votes: 5 },
        { tagA: "Candidate", tagB: "Org management", title: "Calendar management", votes: 3 },
        { tagA: "Idea", tagB: "Training", title: "Employee procedures", votes: 1 },
      ],
      mockupRealtime: "Real-time",
      mockupRealtimeBody: "Submitted votes appear instantly to the product team.",
      ctaButton: "See how it looks",
    },
  },
};

export function getProposalTexts(language: ProposalLanguage): ProposalTexts {
  return language === "en" ? EN : LT;
}
